package one.healthium.onboarding.ui.screens

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.imePadding
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import loco.GENERAL_ERROR_MAX_VALUE
import loco.GENERAL_ERROR_MIN_VALUE
import loco.GENERAL_KG_LABEL
import loco.GENERAL_LB_LABEL
import loco.Res
import one.healthium.base.utils.kgToLb
import one.healthium.domain.user.models.bmiRange
import one.healthium.onboarding.domain.configs.WeightInputScreenConfig
import one.healthium.onboarding.ui.UserDataProvider
import one.healthium.onboarding.ui.UserProfileUiModel
import one.healthium.onboarding.ui.bindPlaceholderValues
import one.healthium.onboarding.ui.components.DynamicSpacer
import one.healthium.onboarding.ui.components.WeightInputTextFiled
import one.healthium.onboarding.ui.models.WeightUnit
import one.healthium.ui.components.CtaButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppSegmentedButtonRow
import one.healthium.ui.compose.components.AppText
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.gray_3
import org.jetbrains.compose.resources.stringResource

interface WeightInputEventDelegate {
    fun onNextClicked()
}

@Composable
internal fun WeightInputScreen(
    eventDelegate: WeightInputEventDelegate,
    screenConfig: WeightInputScreenConfig,
) {
    UserDataProvider { userState, contract ->
        AppScaffold {
            ProfileWeightScreenContent(
                screenConfig = screenConfig,
                userState = userState,
                onNextClicked = eventDelegate::onNextClicked,
                onWeightChanged = contract::onWeightChanged,
                onWeightUnitChanged = contract::onWeightUnitChanged,
            )
        }
    }
}

@Composable
internal fun ProfileWeightScreenContent(
    modifier: Modifier = Modifier,
    screenConfig: WeightInputScreenConfig,
    userState: UserProfileUiModel,
    onNextClicked: () -> Unit,
    onWeightChanged: (Float?) -> Unit,
    onWeightUnitChanged: (WeightUnit) -> Unit,
) {
    val weight = userState.weight
    val weightUnit = userState.selectedWeightUnit

    Column(
        modifier = modifier
            .fillMaxSize()
            .statusBarsPadding()
            .navigationBarsPadding()
            .padding(horizontal = 24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = screenConfig.title?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.header,
            color = AppTheme.colors.text100,
            textAlign = TextAlign.Center,
        )
        Spacer(Modifier.height(24.dp))
        DynamicSpacer(0.6f)
        WeightInputTextFiled(
            modifier = Modifier.fillMaxWidth(),
            weight = weight,
            weightUnit = weightUnit,
            onWeightChanged = onWeightChanged,
        )
        Spacer(Modifier.height(24.dp))
        AppSegmentedButtonRow(
            options = listOf(
                stringResource(Res.string.GENERAL_LB_LABEL),
                stringResource(Res.string.GENERAL_KG_LABEL),
            ),
            selected = when (weightUnit) {
                WeightUnit.Lb -> 0
                WeightUnit.Kg -> 1
            },
            onOptionSelected = { index ->
                val updatedWeightUnit = when (index) {
                    0 -> WeightUnit.Lb
                    1 -> WeightUnit.Kg
                    else -> null
                }
                updatedWeightUnit?.let { onWeightUnitChanged(it) }
            }
        )
        Spacer(Modifier.height(24.dp))
        Box(
            modifier = Modifier.weight(1f)
        ) {
            Caption(userState, screenConfig)
        }
        CtaButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 14.dp),

            text = screenConfig.ctaTitle,
            enabled = weight.isValid(weightUnit, screenConfig),
            onClick = onNextClicked,
        )
        Spacer(Modifier.height(24.dp))
        Spacer(Modifier.navigationBarsPadding().imePadding())
    }
}

@Composable
private fun Caption(
    userState: UserProfileUiModel,
    screenConfig: WeightInputScreenConfig,
) {
    val weight = userState.weight
    val weightUnit = userState.selectedWeightUnit
    val bmiRange = userState.model.bmiRange

    val validationCaption = getValidationCaption(weight, weightUnit, screenConfig)

    if (validationCaption != null) {
        AppText(
            text = validationCaption.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.caption,
            color = gray_3,
            textAlign = TextAlign.Center,
        )
        return
    }

    return Column(
        modifier = Modifier.fillMaxWidth(),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        AppText(
            text = screenConfig.bmiTitle[bmiRange]?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.captionBold,
            color = gray_3,
            textAlign = TextAlign.Center,
        )
        AppText(
            text = screenConfig.bmiText[bmiRange]?.bindPlaceholderValues(userState),
            style = AppTheme.textStyles.caption,
            color = gray_3,
            textAlign = TextAlign.Center,
        )
    }
}

@Composable
private fun getValidationCaption(
    weight: Float?,
    weightUnit: WeightUnit?,
    screenConfig: WeightInputScreenConfig,
): String? {
    if (weight == null || weightUnit == null) {
        return screenConfig.defaultHint.orEmpty()
    }

    when (weightUnit) {
        WeightUnit.Lb -> {
            val weightInLb = weight.kgToLb()
            if (weightInLb < screenConfig.minWeightLb) {
                return stringResource(
                    Res.string.GENERAL_ERROR_MIN_VALUE,
                    screenConfig.minWeightLb
                )
            } else if (weightInLb > screenConfig.maxWeightLb) {
                return stringResource(
                    Res.string.GENERAL_ERROR_MAX_VALUE,
                    screenConfig.maxWeightLb
                )
            }
        }

        WeightUnit.Kg -> {
            if (weight < screenConfig.minWeightKg) {
                return stringResource(
                    Res.string.GENERAL_ERROR_MIN_VALUE,
                    screenConfig.minWeightKg
                )
            } else if (weight > screenConfig.maxWeightKg) {
                return stringResource(
                    Res.string.GENERAL_ERROR_MAX_VALUE,
                    screenConfig.maxWeightKg
                )
            }
        }
    }

    return null
}

private fun Float?.isValid(weightUnit: WeightUnit, screenConfig: WeightInputScreenConfig): Boolean {
    if (this == null) return false

    return when (weightUnit) {
        WeightUnit.Lb -> {
            val valueInLb = this.kgToLb()
            valueInLb >= screenConfig.minWeightLb && valueInLb <= screenConfig.maxWeightLb
        }

        WeightUnit.Kg -> {
            this >= screenConfig.minWeightKg && this <= screenConfig.maxWeightKg
        }
    }
}
